// Libraries
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
// Assets
import { getLatestCreatedDraw, postNewDraw } from '@/api/Sherpa/Draw';
import type { PostNewDrawPayload } from '@/api/Sherpa/Draw/types';
import Button from '@/components/UI/Button';
// custom components
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactMultiSelect, HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { SherpaProjectReactQueryKey } from '@/constants/ReactQueryKeys';
import { useDrawTemplateFileUpload } from '@/hooks/useDrawTemplateFileUpload ';
import { notifyUser } from '@/store/Toast';
import type { UserOption } from '@/types/Constants';
import { daysCountV2, formatDate2, getAcceptedFileTypes, sanitizeResponseError } from '@/utils';
import { isDrawApproverOptionEqual, useDrawApproverOptions } from '../hooks/useDrawApproverOptions';
import { validationSchema } from './validationSchema';

interface NewDrawProps {
  handleModal: () => void;
  projectName: string;
}

const drawDefaultValues = {
  openDate: '',
  closeDate: '',
  length: 0,
  invoiceApproverId: null,
  documentApproverId: null,
  documentViewers: [],
};

const NewDrawBase = ({ handleModal, projectName }: NewDrawProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { projectId } = useParams();
  const { options: approverOptions, isLoading: isApproverOptionsLoading } = useDrawApproverOptions(Number(projectId));

  const templateFileRef = useRef<File[]>(null);
  const signAndUploadFiles = useDrawTemplateFileUpload();

  const { watch, setValue } = useFormContext();
  const openDate = watch('openDate');
  const closeDate = watch('closeDate');

  useEffect(() => {
    if (signAndUploadFiles.uploadSuccess) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Draw updated successfully',
          toastType: 'success',
        })
      );
      handleModal();
    }
  }, [signAndUploadFiles.uploadSuccess, handleModal, dispatch]);

  useEffect(() => {
    const totalDate = closeDate && openDate ? daysCountV2(closeDate, openDate) : 0;
    setValue('length', totalDate < 0 ? 0 : totalDate);
  }, [openDate, closeDate, setValue]);

  const addDraw = useMutation((data: PostNewDrawPayload) => postNewDraw({ data, projectId: Number(projectId) }), {
    onSuccess: (res) => {
      //Tanstack: version 5 query implementation
      queryClient.invalidateQueries({ queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DRAWS] });
      if (templateFileRef.current) {
        signAndUploadFiles.uploadTemplate({
          templateFile: templateFileRef.current,
          drawId: Number(res?.data?.id),
          projectId: Number(projectId),
        });
      }
      handleModal();
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Project Draw created successfully',
          toastType: 'success',
        })
      );
    },
    onError: (error: any) => {
      const resError = sanitizeResponseError(error);
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: resError,
          toastType: 'failed',
        })
      );
    },
  });

  //Tanstack: version 5 query implementation
  const fetchLatestDraw = useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_LATEST_DRAW, projectId],
    queryFn: () =>
      getLatestCreatedDraw({
        projectId: Number(projectId),
      }),
  });
  const _latestDraw: { openDate: string; closeDate: string } | undefined = fetchLatestDraw?.data?.data;

  const submitNewDrawHandler = (data: any) => {
    const { templateDocuments } = data;
    const transformedData: PostNewDrawPayload = {
      openDate: formatDate2(new Date(data.openDate).toISOString()),
      closeDate: formatDate2(new Date(data.closeDate).toISOString()),
      invoiceApproverId: Number(data.invoiceApproverId.id),
      documentApproverId: Number(data.documentApproverId.id),
      viewerIds: data?.documentViewers?.map((viewer: { id: number }) => Number(viewer.id)) ?? [],
    };

    if (templateDocuments?.length) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      templateDocuments?.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(',');
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(', ')} file types are not accepted to upload.`,
            toastType: 'failed',
          })
        );
      }

      if (validFiles.length > 0) {
        templateFileRef.current = validFiles;
      }
    }

    addDraw.mutate(transformedData);
  };

  const minDate = (date: string | number | Date | undefined) => {
    if (date) {
      return dayjs(date).utc().startOf('day').add(1, 'day').format('YYYY-MM-DD');
    } else {
      return dayjs().utc().startOf('day').format('YYYY-MM-DD');
    }
  };

  return (
    <HookForm onSubmit={submitNewDrawHandler} className='space-y-2 grid grid-cols-2 gap-4'>
      <h3 className='text-sm col-span-2'>Project Name: {projectName}</h3>
      <HookInput className='col-span-1' type='date' label='Open Date' name='openDate' required disabled={fetchLatestDraw.isLoading} />
      <HookInput className='col-span-1' type='date' label='Close Date' name='closeDate' min={minDate(openDate)} required />
      <HookInput className='col-span-1' type='text' label='Draw Length (in days)' name='length' disabled />
      <div className='col-span-2'>
        <HookFileInput label='Template Documents' name='templateDocuments' id='templateDocuments' multiple />
      </div>
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Invoice Approver'
        name='invoiceApproverId'
        options={approverOptions}
        getOptionLabel={(option) => (option as UserOption).name}
        getOptionValue={(option) => (option as UserOption).id.toString()}
        isOptionEqualToValue={isDrawApproverOptionEqual}
        placeholder='Select...'
        isLoading={isApproverOptionsLoading}
        required
      />
      <HookReactSingleSelect
        componentClassName='col-span-2'
        label='Document Approver'
        name='documentApproverId'
        options={approverOptions}
        getOptionLabel={(option) => (option as UserOption).name}
        getOptionValue={(option) => (option as UserOption).id.toString()}
        isOptionEqualToValue={isDrawApproverOptionEqual}
        placeholder='Select...'
        isLoading={isApproverOptionsLoading}
        required
      />
      <div className='col-span-2'>
        <HookReactMultiSelect
          name='documentViewers'
          label='Document Viewer'
          options={approverOptions}
          getOptionLabel={(option) => (option as UserOption).name}
          getOptionValue={(option) => (option as UserOption).id.toString()}
          isOptionEqualToValue={isDrawApproverOptionEqual}
          placeholder='Select...'
          isLoading={isApproverOptionsLoading}
          required
        />
      </div>
      <div className='flex gap-4 mt-5 justify-end col-span-2'>
        <Button
          label='Cancel'
          type='button'
          btnStyle='btn-neutral'
          disabled={addDraw.isLoading || signAndUploadFiles.isLoading || isApproverOptionsLoading}
          onClick={handleModal}
        />
        <Button
          label={`${addDraw.isLoading || signAndUploadFiles.isLoading ? 'Creating...' : 'Create New Draw'}`}
          type='submit'
          btnStyle='btn-primary'
          disabled={addDraw.isLoading || signAndUploadFiles.isLoading || isApproverOptionsLoading}
        />
      </div>
    </HookForm>
  );
};

export const NewDraw = (props: NewDrawProps) => {
  return (
    <Modal>
      <h2 className='mt-0'>New Draw</h2>
      <HookFormProvider defaultValues={drawDefaultValues} resolver={yupResolver(validationSchema) as any}>
        <NewDrawBase {...props} />
      </HookFormProvider>
    </Modal>
  );
};
